import { useQuery } from "@tanstack/react-query";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { twJoin } from "tailwind-merge";

import type { FeelingAtHomeWidgetV2Dto } from "@/api/types";
import { Button } from "@/design-system/Button";
import { cardSurfaceVariants } from "@/design-system/Card";
import { Stack } from "@/design-system/Stack";
import { SUPPORT_EMAIL } from "@/helpers/constants";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import type { DateRange } from "@/queries/analytics/queryOptions";
import { useAnalyticsQueries } from "@/queries/analytics/queryOptions";
import { useTranslation } from "@/translations";

import { AnalyticsWidgetStates } from "../AnalyticsWidgetStates";
import { FahWidgetGraphics } from "./FahWidgetGraphics";
import { FahWidgetSpeedometer } from "./FahWidgetSpeedometer";
import { FahWidgetTrendChart } from "./FahWidgetTrendChart";

const FALLBACK_RECENT_FAH_STATS: FeelingAtHomeWidgetV2Dto = {
  hasFeelAtHomeDetailsLink: false,
  status: "notStarted",
  benchmarkFeelingAtHomeScore: {
    rating: 0,
  },
  distributions: [],
};

export function FahWidget(): React.ReactNode {
  const [selectedTrendChartPeriod, setSelectedTrendChartPeriod] = useState<DateRange>("last6Months");

  const sessionUser = useSessionUser();
  const { t } = useTranslation();
  const analyticsQueries = useAnalyticsQueries();
  const {
    data: fahRecentStats,
    isPending: isPendingFahRecentStats,
    isError: isErrorFahRecentStats,
    error: errorFahRecentStats,
    refetch: refetchFahRecentStats,
  } = useQuery(analyticsQueries.fah());
  const {
    data: fahHistoricStats,
    isPending: isPendingFahHistoricStats,
    isError: isErrorFahHistoricStats,
    error: fahHistoricStatsError,
    refetch: refetchFahHistoricStats,
  } = useQuery(analyticsQueries.fahMonthlyStats(selectedTrendChartPeriod));

  const onReloadData = () => {
    void refetchFahRecentStats();
    void refetchFahHistoricStats();
  };

  const isFahNotStarted = !isPendingFahRecentStats && !isErrorFahRecentStats && fahRecentStats.status === "notStarted";

  return (
    <article className="flex w-full items-end overflow-hidden rounded-lg bg-background-brand-secondary p-space-md @xl:p-space-lg @xl:pr-0">
      <section
        className={twJoin(
          "relative flex w-full flex-1 shrink-0 flex-col items-center overflow-hidden",
          cardSurfaceVariants(),
        )}
      >
        {/* Title */}
        <header className="w-full p-space-md pb-0 @xl:p-space-lg @xl:pb-0">
          <h1 className="text-center text-heading-2 @xl:text-left @xl:text-heading-1">
            {`${t("component.analytics-widget.fah.title.greeting", { name: sessionUser.firstName })} 👋`}
            <span className="text-heading-2-light @xl:text-heading-1-light">{` ${t("component.analytics-widget.fah.title")}`}</span>
          </h1>
        </header>
        {/* Body */}
        <section className="flex w-full flex-col *:first:border-b *:first:border-border-brand-secondary @md:grid @md:grid-cols-2 @md:grid-rows-1 @md:*:first:border-none">
          <section className="relative w-full overflow-hidden">
            <FahWidgetSpeedometer data={fahRecentStats || FALLBACK_RECENT_FAH_STATS} />
            <AnimatePresence>
              {isPendingFahRecentStats && <AnalyticsWidgetStates.Loading />}
              {isErrorFahRecentStats && (
                <AnalyticsWidgetStates.Error onReload={onReloadData} error={errorFahRecentStats} />
              )}
            </AnimatePresence>
          </section>
          <section className="relative w-full overflow-hidden">
            <FahWidgetTrendChart
              data={fahHistoricStats || []}
              selectedPeriod={selectedTrendChartPeriod}
              onChangePeriod={setSelectedTrendChartPeriod}
            />
            <AnimatePresence>
              {isPendingFahHistoricStats && <AnalyticsWidgetStates.Loading />}
              {isErrorFahHistoricStats && (
                <AnalyticsWidgetStates.Error onReload={onReloadData} error={fahHistoricStatsError} />
              )}
            </AnimatePresence>
          </section>
        </section>
        {/* Shared empty state */}
        <AnimatePresence>{isFahNotStarted && <EmptyState />}</AnimatePresence>
      </section>

      <aside className="relative -right-space-xl hidden w-search-wide shrink-0 @xl:block">
        <FahWidgetGraphics image={sessionUser.project.backgroundImage} />
      </aside>
    </article>
  );
}

function EmptyState() {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.2, ease: "linear" }}
      className="absolute inset-0 z-raised flex flex-col items-center justify-between gap-space-md bg-background-default/90 p-space-md backdrop-blur-xs"
    >
      <Stack gap="xs">
        <h2 className="text-center text-heading-2">{t("component.analytics-widget.fah.empty.title")}</h2>
        <p className="text-center">{t("component.analytics-widget.fah.empty.description")}</p>
      </Stack>
      <Button type="link" href={`mailto:${SUPPORT_EMAIL}?subject=Request to start FAH survey`} isExternal>
        {t("component.analytics-widget.fah.get-help-success.btn")}
      </Button>
    </motion.div>
  );
}
